import { Link } from "react-router-dom";
import { Page } from "@/components/common";

export const CONTACT_EMAIL = "support@yapprapp.com";
const UPDATED = "June 2026";

const Doc = ({ title, testId, children }) => (
  <Page className="max-w-3xl">
    <p className="font-mono text-xs uppercase tracking-widest text-stone-500">Last updated {UPDATED}</p>
    <h1 data-testid={testId} className="font-display text-4xl sm:text-5xl font-black tracking-tight">{title}</h1>
    <div className="mt-10 space-y-8 text-stone-700 leading-relaxed [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-stone-900 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
      {children}
      <section>
        <h2>Contact</h2>
        <p>Questions? Contact Yappr LLC at <a href={`mailto:${CONTACT_EMAIL}`} data-testid="legal-contact-email" className="font-semibold text-[#C2410C]">{CONTACT_EMAIL}</a>.</p>
      </section>
    </div>
  </Page>
);

export const PrivacyPage = () => (
  <Doc title="Privacy Policy" testId="privacy-title">
    <p>This policy explains what Creator Series, operated by Yappr LLC ("we", "us"), collects when you use our website and mobile app, which share one account system, and how we use it.</p>
    <section>
      <h2>What we collect</h2>
      <ul>
        <li>Account details: name, email, password (stored hashed), city, and optional profile info such as photo, bio, age, gender and social handles.</li>
        <li>Event activity: events you host, tickets you buy, check-ins, reviews, follows and photos you upload.</li>
        <li>Location: the ZIP code you search with, or your device location if you allow it in the app, so we can show nearby events.</li>
        <li>Payments: tickets are paid through Stripe. We never see or store your full card number.</li>
      </ul>
    </section>
    <section>
      <h2>How we use it</h2>
      <ul>
        <li>To run your account, sell and verify tickets, and show you events near you.</li>
        <li>To send notifications and updates you opt into (email or text).</li>
        <li>To keep the community safe and prevent fraud.</li>
      </ul>
    </section>
    <section>
      <h2>What hosts can see</h2>
      <p>When you get a ticket, the host sees your name and profile. Your email or phone number is shared only if you tick the matching box at checkout.</p>
    </section>
    <section>
      <h2>Sharing</h2>
      <p>We don't sell your personal information. We share data only with service providers that help us run Creator Series (hosting, payments, email/SMS, maps), or when the law requires it.</p>
      <p className="mt-3" data-testid="privacy-sms-clause">Mobile numbers and SMS opt-in consent will not be shared with third parties or affiliates for marketing or promotional purposes.</p>
    </section>
    <section>
      <h2>Your choices</h2>
      <ul>
        <li>Edit your profile and notification settings at any time.</li>
        <li>Delete your account from the <Link to="/account/delete" className="font-semibold text-[#C2410C]">delete account page</Link> or by emailing us. Some payment records may be kept as the law requires.</li>
      </ul>
    </section>
    <section>
      <h2>Children</h2>
      <p>Creator Series (Yappr LLC) is not intended for anyone under 13, and we don't knowingly collect their data.</p>
    </section>
    <section>
      <h2>Changes</h2>
      <p>If we update this policy, we'll change the date above and notify you about significant changes.</p>
    </section>
  </Doc>
);

export const SmsPage = () => (
  <Doc title="Creator Series Text Message Program" testId="sms-title">
    <p>Creator Series, operated by Yappr LLC, sends text messages only to users who opt in.</p>
    <section>
      <h2>How to opt in</h2>
      <p>In the Creator Series app, go to <strong>Profile → Settings → Text notifications</strong>, enter your mobile number, and tap <strong>"Save number."</strong> Opting in is optional and is not required to buy tickets.</p>
    </section>
    <section>
      <h2>What you'll receive</h2>
      <p>A reminder the day before events you have tickets for, and an alert if one of your events is cancelled, including refund details. We do not send marketing messages. Message frequency varies with the number of events you attend.</p>
    </section>
    <section>
      <h2>Rates, opt-out and help</h2>
      <p>Msg &amp; data rates may apply. Reply <strong>STOP</strong> at any time to opt out. Reply <strong>HELP</strong> for help. You can also remove your number or turn off Text notifications in the app's Settings.</p>
    </section>
    <section>
      <h2>Privacy</h2>
      <p>We never share or sell your mobile number to third parties or affiliates for marketing. See our <Link to="/privacy" data-testid="sms-privacy-link" className="font-semibold text-[#C2410C]">Privacy Policy</Link> and <Link to="/terms" data-testid="sms-terms-link" className="font-semibold text-[#C2410C]">Terms</Link>.</p>
    </section>
    <section>
      <h2>Opt-in screen</h2>
      <p className="mb-4">The Settings → Text notifications screen in the Creator Series app:</p>
      <img data-testid="sms-optin-screenshot" src="/sms-optin.jpg" alt="Creator Series app Settings screen with Text notifications toggle, mobile number field and Save number button"
        className="w-full max-w-[320px] rounded-[2rem] border-8 border-stone-900 shadow-xl" />
    </section>
  </Doc>
);

export const TermsPage = () => (
  <Doc title="Terms of Service" testId="terms-title">
    <p>Creator Series is operated by Yappr LLC ("Yappr", "we", "us"). These terms are an agreement between you and Yappr LLC. By using the Creator Series website or app you agree to them.</p>
    <section>
      <h2>Your account</h2>
      <p>You must give accurate information and keep your password safe. You're responsible for activity on your account. One account works on both the website and the app.</p>
    </section>
    <section>
      <h2>Tickets and payments</h2>
      <ul>
        <li>Tickets are sold by event hosts and paid through Stripe.</li>
        <li>If a host cancels an event, ticket holders are refunded.</li>
        <li>Other refunds are up to the host unless the law requires otherwise.</li>
      </ul>
    </section>
    <section>
      <h2>Hosting events</h2>
      <p>Hosts are responsible for their events: accurate descriptions, a safe venue, and following local laws and venue rules. Creator Series and Yappr LLC provide the platform and don't organize or run the events.</p>
    </section>
    <section>
      <h2>Community rules</h2>
      <ul>
        <li>No harassment, hate, threats, or illegal activity.</li>
        <li>Only post photos and content you have the right to share.</li>
        <li>We may remove content or suspend accounts that break these rules.</li>
      </ul>
    </section>
    <section>
      <h2>Your content</h2>
      <p>You own what you post. You give Yappr LLC permission to display it on Creator Series so the service can work.</p>
    </section>
    <section>
      <h2>Liability</h2>
      <p>Creator Series is provided "as is". To the extent the law allows, Yappr LLC isn't liable for what happens at events, or for indirect losses from using the service.</p>
    </section>
    <section>
      <h2>Ending your account</h2>
      <p>You can delete your account at any time. We may suspend accounts that break these terms.</p>
    </section>
  </Doc>
);
